import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedActions from '../components/FixedActions';
import { ArrowRight, Award, Users, Calendar, MapPin, Phone, Mail, Target, Shield, Clock, Star, ExternalLink } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [videosLoaded, setVideosLoaded] = React.useState({ left: false, right: false });

  // Hide loading screen only when BOTH videos are ready
  useEffect(() => {
    if (videosLoaded.left && videosLoaded.right) {
      // Add a small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setIsLoading(false);
        
        // Start both videos together after loading screen fades
        setTimeout(() => {
          const leftVideo = leftVideoRef.current;
          const rightVideo = rightVideoRef.current;
          
          if (leftVideo && rightVideo) {
            // Synchronize video start
            Promise.all([
              leftVideo.play().catch(err => console.log('Left video play failed:', err)),
              rightVideo.play().catch(err => console.log('Right video play failed:', err))
            ]);
          }
        }, 300); // Small delay for fade animation
      }, 500); // Minimum display time for loading screen
      
      return () => clearTimeout(timer);
    }
  }, [videosLoaded]);

  useEffect(() => {
    const leftVideo = leftVideoRef.current;
    const rightVideo = rightVideoRef.current;
    
    const initVideo = (video, skipBlackFrames = false, videoSide = 'left') => {
      if (video) {
        const markAsReady = () => {
          // Prepare video but don't play yet
          if (skipBlackFrames) {
            video.currentTime = 0.1;
          } else {
            video.currentTime = 0;
          }
          
          // Pause to ensure it doesn't autoplay before we're ready
          video.pause();
          
          // Mark video as loaded
          setVideosLoaded(prev => ({ ...prev, [videoSide]: true }));
        };

        // Wait for video to be ready to play
        if (video.readyState >= 3) {
          // HAVE_FUTURE_DATA - enough to start playing
          markAsReady();
        } else {
          video.addEventListener('canplaythrough', markAsReady, { once: true });
        }

        // Ensure smooth loop without black frames
        video.addEventListener('ended', () => {
          if (skipBlackFrames) {
            video.currentTime = 0.1;
          } else {
            video.currentTime = 0;
          }
          video.play();
        });
      }
    };

    // Initialize videos - right video skips black frames
    initVideo(leftVideo, false, 'left');
    initVideo(rightVideo, true, 'right');

    const handleScroll = () => {
      [leftVideo, rightVideo].forEach(video => {
        if (video) {
          const rect = video.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          
          if (isVisible && video.paused && !isLoading) {
            video.play().catch(err => console.log('Video play failed:', err));
          } else if (!isVisible && !video.paused) {
            video.pause();
          }
        }
      });
    };

    // Initial load
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (leftVideo) leftVideo.pause();
      if (rightVideo) rightVideo.pause();
    };
  }, [isLoading]);

  const stats = [
    { number: '100+', label: 'Projects' },
    { number: '50+', label: 'Clients' },
    { number: '5+', label: 'Cities' }
  ];

  const clientLogos = [
    { name: 'Hive Club', logo: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/2o18uiv9_HIVE%20CLUB.jpg' },
    { name: 'Ministry of Youth Affairs and Sports', logo: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/10e95jiq_Ministry%20of%20youth%20affairs%20and%20sports.jpg' },
    { name: 'Decathlon', logo: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/o2dwbusq_Decathlon.jpg' },
    { name: 'Klutch', logo: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/wk4bn4x4_Klutch.jpg' },
    { name: 'Pickle Park', logo: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/cafam7kv_Pickle%20park.jpg' },
    { name: 'Athlevate', logo: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/0e4z0cee_Athlevate.jpg' }
  ];

  const featuredProjects = [
    {
      title: 'Decathlon (Pickleball Court)',
      location: 'Bangalore',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/3nqpjjlb_Pickleball%20Courts%20Decathlon%201.jpg',
      description: 'Professional pickleball court installation at Decathlon sports store with regulation specifications and high-quality materials.'
    },
    {
      title: 'Baglur Sports Arena (11A Side Football Turf)',
      location: 'Baglur, Bangalore',
      image: 'https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/o8vtl0v7_Football%20Court%20Baglur%20Sportss%20arena%20%201.jpg',
      description: 'Professional 11-a-side football turf with FIFA-standard specifications and premium artificial turf.'
    }
  ];

  const surfaces = [
    { name: 'Pickleball', icon: '🏓' },
    { name: 'Padel', icon: '🎾' },
    { name: 'Badminton', icon: '🏸' },
    { name: 'Football', icon: '⚽' },
    { name: 'Tennis', icon: '🎾' },
    { name: 'Basketball', icon: '🏀' },
    { name: 'Cricket', icon: '🏏' },
    { name: 'Volleyball', icon: '🏐' },
    { name: 'Squash', icon: '🎯' },
    { name: 'Athletic Track', icon: '🏃' }
  ];

  return (
    <div>
      <Header />
      <FixedActions />
      
      {/* Loading Screen */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          background: 'linear-gradient(135deg, #1A1B1E 0%, #2D2E32 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          transition: 'opacity 0.8s ease-out',
          opacity: isLoading ? 1 : 0
        }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_8df07f88-bc0e-485a-bdfa-23d100a34040/artifacts/yx81ddm2_rjlogo%20%283%29.png" 
            alt="Right Shade Logo" 
            style={{
              height: '120px',
              marginBottom: '30px',
              animation: 'pulse 2s ease-in-out infinite'
            }}
          />
          <p style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '16px',
            marginBottom: '20px',
            fontFamily: "'Inter', sans-serif"
          }}>
            Loading Experience...
          </p>
          <div style={{
            width: '200px',
            height: '3px',
            background: 'rgba(255, 215, 0, 0.2)',
            borderRadius: '10px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              background: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
              animation: 'shimmer 1.5s ease-in-out infinite'
            }}></div>
          </div>
        </div>
      )}
      
      {/* Hero Section with Full Height Videos */}
      <section className="hero-section" style={{ 
        position: 'relative',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,255,255,1) 100%)',
        paddingTop: '80px',
        overflow: 'hidden'
      }}>
        {/* Left Video - From Header to Bottom */}
        <div className="hero-video-left" style={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100vh',
          paddingTop: '80px',
          zIndex: 1
        }}>
          <div style={{
            height: 'calc(100vh - 80px)',
            aspectRatio: '4/5',
            borderRadius: '0 24px 24px 0',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #2D2E32 0%, #1A1B1E 100%)'
          }}>
            <video
              ref={leftVideoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                backgroundColor: '#2D2E32'
              }}
            >
              <source src="https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/fh9pty9m_Sports%20infrastructure%20in%20bangalore.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Right Video - From Header to Bottom */}
        <div className="hero-video-right" style={{
          position: 'absolute',
          right: 0,
          top: 0,
          height: '100vh',
          paddingTop: '80px',
          zIndex: 1
        }}>
          <div style={{
            height: 'calc(100vh - 80px)',
            aspectRatio: '4/5',
            borderRadius: '24px 0 0 24px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #2D2E32 0%, #1A1B1E 100%)'
          }}>
            <video
              ref={rightVideoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                backgroundColor: '#2D2E32'
              }}
            >
              <source src="https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/li1dzt38_Best%20construction%20in%20bangalore.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Center Content - Middle Space */}
        <div className="hero-content" style={{ 
          position: 'relative',
          width: '100%',
          minHeight: 'calc(100vh - 80px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center', 
          zIndex: 2,
          padding: '0 calc((100vh - 80px) * 4 / 5 + 40px)'
        }}>
          <div>
            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: 700,
              lineHeight: 1.2,
              marginBottom: '20px',
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: "'Poppins', 'Inter', 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              letterSpacing: '-0.02em'
            }}>
              Building Excellence in Sports & Construction
            </h1>
            
            <p style={{
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
              color: '#4A5568',
              lineHeight: 1.6,
              fontFamily: "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              fontWeight: 400,
              letterSpacing: '-0.01em',
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              Right Shade brings together a team of highly experienced professionals in sports construction and building development, delivering world-class facilities across India.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section style={{ padding: '80px 0', background: '#FAFBFC' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '60px', alignItems: 'center', marginBottom: '30px' }}>
            <div>
              <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500&h=400&fit=crop" 
                   alt="What We Do" 
                   style={{ width: '100%', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }} />
            </div>
            <div>
              <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', fontWeight: 600, marginBottom: '24px', color: '#1A1B1E' }}>
                What We Do
              </h2>
              <p style={{ fontSize: '18px', lineHeight: 1.7, color: '#4A5568', marginBottom: '30px' }}>
                Right Shade brings together a team of highly experienced and dedicated professionals in sports infrastructure and construction, committed to delivering technically sound, commercially viable, and beautifully designed sporting facilities across India.
              </p>
              <p style={{ fontSize: '16px', lineHeight: 1.6, color: '#718096' }}>
                We specialize in all aspects of sports construction, including football turf construction, tennis court construction, cricket turf construction, badminton court construction, basketball court construction, and athletic track construction ensuring comprehensive coverage for all sports flooring and infrastructure projects, from design to execution.
              </p>
            </div>
          </div>

          {/* Stats Row */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '40px',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {stats.map((stat, index) => (
              <div key={index}>
                <div style={{ 
                  fontSize: 'clamp(2.5rem, 5vw, 4rem)', 
                  fontWeight: 800, 
                  color: '#FFD700',
                  marginBottom: '8px'
                }}>
                  {stat.number}
                </div>
                <div style={{ fontSize: '18px', color: '#4A5568', fontWeight: 500 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Landscape Banner Section - 10:2 Ratio */}
      {/* Banner Section */}
      <section style={{ padding: '0', background: '#FAFBFC' }}>
        <div style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_remove-emergent-3/artifacts/5swzd37b_Champions%20are%20Built%20on%20the%20Right%20Ground.jpg"
            alt="Champions are Built on the Right Ground"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              objectFit: 'cover'
            }}
          />
        </div>
      </section>

      {/* Clients Section */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 600, marginBottom: '16px' }}>
              Clients, work & us
            </h2>
            <h3 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 400, color: '#4A5568', marginBottom: '20px' }}>
              Big Things Beautiful & Small Things Brilliant
            </h3>
            <p style={{ fontSize: '18px', color: '#718096', maxWidth: '600px', margin: '0 auto' }}>
              We are immensely proud of our family of diverse clients that have trusted us to support them. Be it a leading education institution or a multinational organisation, we have delivered world-class sports facilities.
            </p>
          </div>

          <div style={{ marginBottom: '40px' }}>
            <p style={{ textAlign: 'center', fontSize: '16px', color: '#718096', marginBottom: '30px' }}>
              Some Industries We've Worked in
            </p>
            <p style={{ textAlign: 'center', fontSize: '14px', color: '#A0AEC0' }}>
              Education | IT | Government | Real Estate | Sports Academies
            </p>
          </div>

          {/* Client Logos Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '30px',
            alignItems: 'center',
            justifyItems: 'center'
          }}>
            {clientLogos.map((client, index) => (
              <div 
                key={index} 
                style={{ 
                  padding: '24px',
                  background: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '100px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(255,215,0,0.2)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                }}
              >
                <img 
                  src={client.logo} 
                  alt={client.name} 
                  style={{ 
                    maxHeight: '60px',
                    maxWidth: '140px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section style={{ padding: '80px 0', background: '#FAFBFC' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 600, marginBottom: '20px' }}>
              Featured Projects
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '40px' }}>
            {featuredProjects.map((project, index) => (
              <div key={index} className="card" style={{ overflow: 'hidden', padding: 0 }}>
                <img src={project.image} alt={project.title} 
                     style={{ width: '100%', height: '250px', objectFit: 'cover' }} />
                <div style={{ padding: '30px' }}>
                  <div style={{ fontSize: '14px', color: '#FFD700', fontWeight: 600, marginBottom: '8px' }}>
                    {project.location}
                  </div>
                  <h3 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px', color: '#1A1B1E' }}>
                    {project.title}
                  </h3>
                  <p style={{ color: '#4A5568', marginBottom: '20px' }}>
                    {project.description}
                  </p>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => navigate('/projects')}
                  >
                    View Details <ExternalLink size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/projects')}
            >
              View All Projects <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Surfaces Section */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 600, marginBottom: '20px' }}>
              Surfaces
            </h2>
            <h3 style={{ fontSize: 'clamp(1.25rem, 3vw, 1.75rem)', fontWeight: 400, color: '#4A5568' }}>
              Deeper than the Surface
            </h3>
            <p style={{ fontSize: '18px', color: '#718096', maxWidth: '600px', margin: '20px auto 0' }}>
              Our team has the experience and know how to install varied sports surfaces to meet international standards.
            </p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: '30px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {surfaces.map((surface, index) => (
              <div key={index} style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '48px', 
                  marginBottom: '16px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {surface.icon}
                </div>
                <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1A1B1E' }}>
                  {surface.name}
                </h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ 
        padding: '100px 0',
        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{ 
            fontSize: 'clamp(2rem, 5vw, 3.5rem)', 
            fontWeight: 700, 
            color: '#1A1B1E',
            marginBottom: '20px'
          }}>
            Step into a field of possibilities
          </h2>
          <p style={{ 
            fontSize: '24px', 
            fontStyle: 'italic', 
            color: '#1A1B1E',
            marginBottom: '40px',
            opacity: 0.8
          }}>
            Get in touch!
          </p>
          <button 
            className="btn" 
            style={{
              background: '#1A1B1E',
              color: 'white',
              padding: '18px 40px',
              fontSize: '18px',
              fontWeight: 600,
              borderRadius: '50px'
            }}
            onClick={() => navigate('/contact')}
          >
            Contact Us <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Google Maps Section */}
      <section style={{ padding: '0', background: '#FAFBFC' }}>
        <div style={{ width: '100%', height: '400px', position: 'relative' }}>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.0673415491916!2d77.63577731476946!3d12.971598290861623!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1609e8c5b0e1%3A0x1b3f4c3b8c5b0e1!2sRT%20Nagar%2C%20Bengaluru%2C%20Karnataka%20560032!5e0!3m2!1sen!2sin!4v1679846123456!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Right Shade Location - RT Nagar, Bengaluru"
          ></iframe>
          
          {/* Map Overlay Info */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            background: 'rgba(255,255,255,0.95)',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            maxWidth: '300px'
          }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#1A1B1E',
              marginBottom: '8px'
            }}>
              Right Shade Office
            </h4>
            <p style={{
              fontSize: '14px',
              color: '#4A5568',
              marginBottom: '8px',
              lineHeight: 1.4
            }}>
              RT Nagar, Bengaluru 560032
            </p>
            <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#718096' }}>
              <span>📞 +91 99646 60033</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <style jsx>{`
        /* Loading Screen Animations */
        @keyframes fadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; visibility: hidden; }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        /* Mobile Hero Layout */
        @media (max-width: 1024px) {
          .hero-section {
            min-height: auto !important;
            padding-top: 80px !important;
            display: flex;
            flex-direction: column;
            padding-bottom: 0 !important;
          }
          
          /* Sports video on top - starts right below header */
          .hero-video-left {
            position: relative !important;
            left: auto !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            padding: 0 !important;
            order: 1;
            margin-top: -80px !important;
            padding-top: 80px !important;
          }
          
          .hero-video-left > div {
            height: 320px !important;
            width: 100% !important;
            aspect-ratio: 16/9 !important;
            border-radius: 0 !important;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1) !important;
          }
          
          /* Text content in middle */
          .hero-content {
            position: relative !important;
            padding: 50px 20px !important;
            min-height: auto !important;
            order: 2;
          }
          
          .hero-content h1 {
            font-size: 1.75rem !important;
            margin-bottom: 16px !important;
          }
          
          .hero-content p {
            font-size: 1rem !important;
            padding: 0 10px;
          }
          
          /* Construction video on bottom */
          .hero-video-right {
            position: relative !important;
            right: auto !important;
            top: auto !important;
            width: 100% !important;
            height: auto !important;
            padding: 0 !important;
            order: 3;
            margin-bottom: 0 !important;
          }
          
          .hero-video-right > div {
            height: 320px !important;
            width: 100% !important;
            aspect-ratio: 16/9 !important;
            border-radius: 0 !important;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1) !important;
          }
        }
        
        @media (max-width: 768px) {
          .hero-section {
            padding-top: 60px !important;
            min-height: auto !important;
          }
          
          /* Sports video starts exactly below header (60px) */
          .hero-video-left {
            margin-top: -60px !important;
            padding-top: 60px !important;
          }
          
          /* Hero section mobile - smaller videos */
          .hero-video-left > div,
          .hero-video-right > div {
            height: 280px !important;
            border-radius: 0 !important;
          }
          
          .hero-content {
            padding: 40px 20px !important;
          }
          
          .hero-content h1 {
            font-size: 1.5rem !important;
          }
          
          .hero-content p {
            font-size: 0.95rem !important;
          }
          
          /* What We Do section */
          section:nth-of-type(2) > div > div:first-child {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
          
          section:nth-of-type(2) > div > div:first-child > div:first-child {
            order: 2;
          }
          
          section:nth-of-type(2) > div > div:first-child > div:last-child {
            order: 1;
            text-align: center;
          }
          
          /* Stats section - keep horizontal on mobile */
          section:nth-of-type(2) > div > div:last-child {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 20px !important;
          }
          
          /* Reduce padding around What We Do section above banner */
          section:nth-of-type(2) {
            padding: 40px 20px 20px 20px !important;
          }
          
          /* Banner Section - reduce gaps above and below */
          section:nth-of-type(3) {
            margin-top: -20px !important;
            margin-bottom: -20px !important;
          }
          
          /* Clients section - reduce top padding */
          section:nth-of-type(4) {
            padding: 40px 20px 60px 20px !important;
          }
          
          /* Client Logos */
          section:nth-of-type(4) > div > div:last-child {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
          
          /* Featured Projects - reduce padding and gaps */
          section:nth-of-type(5) {
            padding: 40px 20px !important;
          }
          
          section:nth-of-type(5) > div > div:first-child {
            margin-bottom: 30px !important;
          }
          
          section:nth-of-type(5) > div > div:first-child h2 {
            margin-bottom: 10px !important;
          }
          
          section:nth-of-type(5) > div > div:nth-child(2) {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
          
          /* Surfaces section - reduce top padding */
          section:nth-of-type(6) {
            padding: 40px 20px 60px 20px !important;
          }
          
          section:nth-of-type(6) > div > div:first-child {
            margin-bottom: 30px !important;
          }
          
          section:nth-of-type(6) > div > div:nth-child(2) {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
          
          /* CTA section */
          section:nth-of-type(7) {
            padding: 60px 20px !important;
          }
          
          section:nth-of-type(7) h2 {
            font-size: 2rem !important;
          }
          
          section:nth-of-type(7) p {
            font-size: 1.25rem !important;
          }
        }
        
        @media (max-width: 480px) {
          .hero-video-left > div,
          .hero-video-right > div {
            height: 240px !important;
          }
          
          .hero-content {
            padding: 35px 15px !important;
          }
          
          .hero-content h1 {
            font-size: 1.35rem !important;
          }
          
          .hero-content p {
            font-size: 0.88rem !important;
          }
          
          /* Client Logos single column on very small screens */
          section:nth-of-type(4) > div > div:last-child {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;